import { RequestBuilder } from "../requestBuilder";
import {
  ActionIdParamsSchema,
  ActionSchema,
  ActionsSyncSchema,
  ChatSchema,
  CreateActionInputSchema,
  DryRunBodySchema,
  ExecutionMetasSchema,
  ExecutionsQueryParamsSchema,
  TransferActionInputSchema,
  UnsubscribedEmailsSchema,
  UpdateActionInputSchema,
} from "./actionClient.types";

// GET /actions-sync - Sync all actions for current focused context
export const ActionsSyncRequestConfig = new RequestBuilder({
  path: () => "/actions-sync",
  responseSchema: ActionsSyncSchema,
}).withIncludeProjectId();

// POST /actions - Create a new action
export const CreateActionRequestConfig = new RequestBuilder({
  path: () => "/actions",
  responseSchema: ActionSchema,
  bodySchema: CreateActionInputSchema,
})
  .withMethod("POST")
  .withIncludeProjectId();

// GET /actions/{actionId} - Get a single action
export const GetActionRequestConfig = new RequestBuilder({
  paramsSchema: ActionIdParamsSchema,
  path: (params) => `/actions/${params.id}`,
  responseSchema: ActionSchema,
}).withIncludeProjectId();

// POST /actions/{actionId} - Update an action
export const UpdateActionRequestConfig = new RequestBuilder({
  paramsSchema: ActionIdParamsSchema,
  path: ({ id }) => `/actions/${id}`,
  responseSchema: ActionSchema,
  bodySchema: UpdateActionInputSchema,
})
  .withMethod("POST")
  .withIncludeProjectId();

// POST /actions/{actionId}/transfer - Transfer ownership of an action to a
// project member, or to the caller when no userId is sent
export const TransferActionRequestConfig = new RequestBuilder({
  paramsSchema: ActionIdParamsSchema,
  path: ({ id }) => `/actions/${id}/transfer`,
  responseSchema: ActionSchema,
  bodySchema: TransferActionInputSchema,
})
  .withMethod("POST")
  .withIncludeProjectId();

// DELETE /actions/{actionId} - Delete an action
export const DeleteActionRequestConfig = new RequestBuilder({
  paramsSchema: ActionIdParamsSchema,
  path: (params) => `/actions/${params.id}`,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeProjectId();

// PUT /actions/{actionId}/dry-run - Execute a dry run of an action
export const DryRunActionRequestConfig = new RequestBuilder({
  paramsSchema: ActionIdParamsSchema,
  path: (params) => `/actions/${params.id}/dry-run`,
  bodySchema: DryRunBodySchema,
  responseSchema: ChatSchema,
})
  .withMethod("PUT")
  .withIncludeProjectId();

// GET /actions - Obtain action executions with optional filters
export const ObtainExecutionsRequestConfig = new RequestBuilder({
  paramsSchema: ExecutionsQueryParamsSchema,
  path: () => "/actions",
  responseSchema: ExecutionMetasSchema,
})
  .withQueryParams((params) => {
    if (!params) return undefined;

    const queryParams: Record<string, string> = {};

    if (params.actionIds && params.actionIds.length > 0) {
      queryParams.actionIds = params.actionIds.join(",");
    }
    if (params.limit !== undefined) {
      queryParams.limit = String(params.limit);
    }
    if (params.after_id !== undefined) {
      queryParams.after_id = params.after_id;
    }
    if (params.before_id !== undefined) {
      queryParams.before_id = params.before_id;
    }

    return Object.keys(queryParams).length > 0 ? queryParams : undefined;
  })
  .withIncludeProjectId();

// GET /actions/{actionId}/unsubscribes - Get unsubscribed emails for an action
export const GetUnsubscribesRequestConfig = new RequestBuilder({
  paramsSchema: ActionIdParamsSchema,
  path: (params) => `/actions/${params.id}/unsubscribes`,
  responseSchema: UnsubscribedEmailsSchema,
}).withIncludeProjectId();

// DELETE /actions/{actionId}/unsubscribes - Resubscribe the current user
export const ResubscribeRequestConfig = new RequestBuilder({
  paramsSchema: ActionIdParamsSchema,
  path: (params) => `/actions/${params.id}/unsubscribes`,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeProjectId();
